import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Tags, Loader2, Check } from "lucide-react";
import { TAXONOMY, subsOf } from "@/lib/productTaxonomy";

const sel = "w-full min-h-[44px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm";

export default function ProductsReclass() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/products/reclass").then((r) => { setRows(r.data); setEdit(Object.fromEntries(r.data.map((p) => [p.id, { category: p.category, subcategory: p.subcategory }]))); }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const ready = (e) => e?.category && (subsOf(e.category).length === 0 || e.subcategory);
  const save = async (ids) => {
    const items = ids.filter((id) => ready(edit[id])).map((id) => ({ id, ...edit[id] }));
    if (!items.length) return toast.error("Choisissez une catégorie et une sous-catégorie");
    setBusy(true);
    try { const { data } = await api.post("/products/reclass", items); toast.success(`${data.done.length} produit(s) reclassé(s)`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const readyIds = (rows || []).map((p) => p.id).filter((id) => ready(edit[id]));
  return (
    <div className="max-w-4xl mx-auto pb-28" data-testid="products-reclass">
      <PageHeader title="Produits à reclasser" subtitle="Anciens produits dont la catégorie était ambiguë — rangez-les en quelques clics" icon={Tags} />
      {rows === null ? <Loader2 className="w-6 h-6 animate-spin text-emerald-600" /> : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="reclass-empty">
          <p className="text-sm text-slate-600">Aucun produit à reclasser.</p>
          <Link to="/stock" className="inline-block mt-3 text-sm text-emerald-700 underline">Retour à Stock & produits</Link>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500" data-testid="reclass-count">{rows.length} produit(s) à reclasser. Rien n'est modifié tant que vous n'avez pas validé.</p>
          {rows.map((p) => {
            const e = edit[p.id] || {}; const subs = subsOf(e.category);
            const set = (patch) => setEdit({ ...edit, [p.id]: { ...e, ...patch } });
            return (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`reclass-row-${p.id}`}>
                <div className="min-w-0"><div className="text-sm font-semibold text-slate-900 break-words">{[p.brand, p.name].filter(Boolean).join(" — ")}</div>
                  <div className="text-[11px] text-slate-500">Catégorie actuelle : {p.legacy_category || "aucune"}</div></div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                  <select data-testid={`reclass-family-${p.id}`} value={e.category || ""} onChange={(ev) => set({ category: ev.target.value, subcategory: "" })} className={sel}>
                    <option value="">Catégorie…</option>{TAXONOMY.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                  </select>
                  <select data-testid={`reclass-sub-${p.id}`} value={e.subcategory || ""} disabled={!subs.length} onChange={(ev) => set({ subcategory: ev.target.value })} className={sel}>
                    <option value="">{subs.length ? "Sous-catégorie…" : "—"}</option>{subs.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button type="button" data-testid={`reclass-save-${p.id}`} disabled={busy || !ready(e)} onClick={() => save([p.id])} className="min-h-[44px] px-4 rounded-lg bg-emerald-500 text-black text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1"><Check className="w-4 h-4" /> Valider</button>
                </div>
              </div>
            );
          })}
          {readyIds.length > 1 && (
            <div className="fixed bottom-4 inset-x-4 sm:static sm:inset-auto z-30">
              <button type="button" data-testid="reclass-save-all" disabled={busy} onClick={() => save(readyIds)} className="w-full min-h-[48px] rounded-xl bg-slate-900 text-white text-sm font-semibold shadow-lg">Valider les {readyIds.length} produits prêts</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
